import { matchMediaType } from '../../../utils';
import type { MediaValue, PictureValueType } from '../../types';
import type {
  TumblrResponseAudioItemType,
  TumblrResponseItemType,
  TumblrResponseLinkItemType,
  TumblrResponsePhotoItemType,
  TumblrResponseTextItemType,
  TumblrResponseVideoItemType
} from '../types';

const getDimension = (match: RegExpExecArray | null, index: number) =>
  match ? parseInt(match[index], 10) : 0;

const parseSrcSet = (srcSet: string) => {
  const srcSetRegex = /(\S+)\s+(\d+)w/g;
  const images: PictureValueType[] = [];

  Array.from(srcSet.matchAll(srcSetRegex)).forEach(([, url]) => {
    const sizeMatch = /(\d+)x(\d+)/.exec(url);
    const width = getDimension(sizeMatch, 1);
    const height = getDimension(sizeMatch, 2);
    images.push({ url, width, height });
  });

  return images;
};

const extractSrcSet = (htmlString: string) => {
  const imgRegex = /srcset="(.*?)"/;

  return htmlString.match(imgRegex)?.[1];
};

const getImages = (imageInContent: string[] | string = '') => {
  const content = Array.isArray(imageInContent)
    ? imageInContent[0]
    : imageInContent;

  const srcSet = extractSrcSet(content);

  return srcSet ? parseSrcSet(srcSet) : [];
};

type MediaTypeInContent =
  | 'galleryInContent'
  | 'imageInContent'
  | 'videoInContent'
  | 'youtubeVideoInContent';

type MediaInTextType = {
  type: MediaTypeInContent;
  content: RegExpMatchArray | RegExpExecArray;
};

const getMediaInText = (rawHtml: string): MediaInTextType | undefined => {
  const imagesInContent = rawHtml.match(/<img [^>]*src="[^"]*"[^>]*>/gm);
  if (imagesInContent) {
    return {
      type: imagesInContent.length > 1 ? 'galleryInContent' : 'imageInContent',
      content: imagesInContent
    };
  }

  const videoInContent = rawHtml.match(
    /<video [^>]*poster="[^"]*"[^>]*><source [^>]*src="[^"]*"[^>]*>/g
  );
  if (videoInContent) {
    return {
      type: 'videoInContent',
      content: videoInContent
    };
  }

  const youtubeVideoInContent = rawHtml.match(
    /<figure [^>]*data-url="[^"]*youtube[^"]*"[^>]*>/g
  );
  const regexp =
    /https?:\/\/(?:www\.)?youtu(?:be\.com\/watch\?v=|\.be\/)([\w\-_]*)(&(amp;)?‌​[\w?‌​=]*)?/gm;
  const youtubeVideoInContentData = youtubeVideoInContent
    ? regexp.exec(youtubeVideoInContent[0])
    : undefined;
  if (youtubeVideoInContentData) {
    return {
      type: 'youtubeVideoInContent',
      content: youtubeVideoInContentData
    };
  }

  return undefined;
};

type TumblrMediaValue = MediaValue<string | PictureValueType[]>;

export const getTumblrMedia = ({
  type: derivedType,
  ...mediaProps
}: TumblrResponseItemType): TumblrMediaValue | undefined => {
  const type = matchMediaType(derivedType);

  if (type === 'image') {
    const { photos } = mediaProps as TumblrResponsePhotoItemType;

    if (photos.length > 1) {
      return {
        type: 'gallery',
        items: photos.map((photo) => ({
          type: 'image',
          src: photo?.alt_sizes
        })),
        thumbnail: photos[0]?.alt_sizes
      };
    }

    return {
      type,
      src: photos[0]?.alt_sizes
    };
  }

  if (type === 'video') {
    const {
      permalink_url: permalinkUrl,
      video_url: videoUrl,
      thumbnail_url: thumbnailUrl
    } = mediaProps as TumblrResponseVideoItemType;

    return {
      type,
      src: permalinkUrl || videoUrl || '',
      thumbnail: thumbnailUrl || ''
    };
  }

  if (type === 'audio') {
    const {
      permalink_url: permalinkUrl,
      audio_url: audioUrl,
      thumbnail_url: thumbnailUrl
    } = mediaProps as TumblrResponseAudioItemType;

    return {
      type: 'video',
      src: permalinkUrl || audioUrl,
      thumbnail: thumbnailUrl ?? ''
    };
  }

  if (type === 'link') {
    const { url, description, title } =
      mediaProps as TumblrResponseLinkItemType;

    return {
      type,
      url,
      description,
      src: '',
      title: title ?? ''
    };
  }

  if (type === 'noMedia' && derivedType === 'text') {
    const { body = '' } = mediaProps as TumblrResponseTextItemType;
    const { type: inContentMediaType, content = [] } =
      getMediaInText(body) ?? {};

    if (inContentMediaType === 'imageInContent') {
      return {
        type: 'image',
        src: getImages(content)
      };
    }

    if (inContentMediaType === 'galleryInContent') {
      return {
        type: 'gallery',
        items: content.map((photo) => ({
          type: 'image',
          src: getImages(photo)
        })),
        thumbnail: getImages(content[0])
      };
    }

    if (inContentMediaType === 'videoInContent') {
      return {
        type: 'video',
        src: content[0]?.replace(/.*src="([^"]*)".*/, '$1') || '',
        thumbnail: content[0]?.replace(/.*poster="([^"]*)".*/, '$1') || ''
      };
    }

    if (inContentMediaType === 'youtubeVideoInContent') {
      return {
        type: 'video',
        src: content[0] || '',
        thumbnail: content[1]
          ? `https://img.youtube.com/vi/${content[1]}/hqdefault.jpg`
          : ''
      };
    }
  }

  return undefined;
};
